import React, { useState, useEffect, useCallback } from 'react';
import { T } from '../utils/theme';
import { fmtBRL, normalizarComanda } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { LargeTitle, SolidCard, SectionHeader, Avatar, HeroCard, StatusBadge } from '../components/ui';
import api from '../services/api';
import { RefreshCcw, Clock, CheckCircle, Phone, Play, X, Ban } from 'lucide-react';

const FilaPage = () => {
  const { user } = useAuth();
  const { servicos, barbeiros, setComandas } = useData();
  const [fila, setFila] = useState([]);
  const [loading, setLoading] = useState(true);
  const [atualizando, setAtualizando] = useState(false);

  // Comanda modal
  const [modalComanda, setModalComanda] = useState(false);
  const [ticketFinalizando, setTicketFinalizando] = useState(null);
  const [srvSel, setSrvSel] = useState([]);
  const [observacoes, setObservacoes] = useState('');
  const [salvandoComanda, setSalvandoComanda] = useState(false);

  // Cancel modal
  const [modalCancelar, setModalCancelar] = useState(false);
  const [ticketCancelando, setTicketCancelando] = useState(null);
  const [motivoCancelamento, setMotivoCancelamento] = useState('');
  const [cancelando, setCancelando] = useState(false);

  const carregarFila = useCallback(async () => {
    try {
      const data = await api.get('/kiosk/barber/queue');
      setFila(data || []);
    } catch (error) {
      console.error('Erro ao carregar fila:', error);
    } finally {
      setLoading(false);
      setAtualizando(false);
    }
  }, []);

  useEffect(() => {
    if (user?.tipo === 'barbeiro') {
      carregarFila();
      const interval = setInterval(carregarFila, 5000);
      return () => clearInterval(interval);
    }
  }, [user, carregarFila]);

  const iniciarAtendimento = async (ticketId) => {
    try {
      await api.patch(`/kiosk/tickets/${ticketId}/start`);
      carregarFila();
    } catch (error) {
      alert('Erro ao iniciar atendimento');
    }
  };

  // --- Finalizar ---
  const abrirComandaFinalizacao = (ticket) => {
    setTicketFinalizando(ticket);
    setSrvSel([]);
    setObservacoes('');
    setModalComanda(true);
  };

  const fecharModalComanda = () => {
    setModalComanda(false);
    setTicketFinalizando(null);
    setSrvSel([]);
    setObservacoes('');
  };

  const handleFinalizar = async () => {
    if (srvSel.length === 0) { alert('Selecione ao menos um servico'); return; }
    setSalvandoComanda(true);
    try {
      const srvs = srvSel.map((id) => {
        const s = servicos.find((sv) => sv.id === id);
        return { id: s?.id || id, nome: s?.nome, preco: parseFloat(s?.preco || 0), comissao: parseFloat(s?.comissao || 0) };
      });
      const total = srvs.reduce((sum, s) => sum + s.preco, 0);
      const nomeCliente = ticketFinalizando?.cliente?.nome || 'Cliente';
      const barbeiroId = user.barbeiroId;

      const resposta = await api.comandas.create({
        nomeCliente,
        barbeiroId,
        servicos: srvs,
        observacoes,
        total,
      });
      const barbeiroSelecionado = barbeiros.find(b => b.id === barbeiroId);
      const comandaNormalizada = normalizarComanda(resposta, { barbeiroNome: barbeiroSelecionado?.nome || resposta?.barbeiroNome });
      setComandas((prev) => [...prev, comandaNormalizada]);

      await api.patch(`/kiosk/tickets/${ticketFinalizando.id}/complete`, { skipComanda: true });
      fecharModalComanda();
      carregarFila();
    } catch (error) {
      alert('Erro: ' + (error.response?.data?.message || 'Desconhecido'));
    } finally {
      setSalvandoComanda(false);
    }
  };

  // --- Cancelar ---
  const abrirCancelamento = (ticket) => {
    setTicketCancelando(ticket);
    setMotivoCancelamento('');
    setModalCancelar(true);
  };

  const fecharModalCancelar = () => {
    setModalCancelar(false);
    setTicketCancelando(null);
    setMotivoCancelamento('');
  };

  const handleCancelar = async () => {
    if (!motivoCancelamento.trim()) { alert('Informe o motivo do cancelamento'); return; }
    setCancelando(true);
    try {
      await api.patch(`/kiosk/tickets/${ticketCancelando.id}/cancel`, { motivo: motivoCancelamento.trim() });
      fecharModalCancelar();
      carregarFila();
    } catch (error) {
      alert('Erro ao cancelar: ' + (error.response?.data?.message || 'Desconhecido'));
    } finally {
      setCancelando(false);
    }
  };

  const toggleServico = (id) => {
    setSrvSel(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const totalComanda = srvSel.reduce((sum, id) => {
    const s = servicos.find(sv => sv.id === id);
    return sum + parseFloat(s?.preco || 0);
  }, 0);

  if (user?.tipo !== 'barbeiro') {
    return (
      <div style={{ padding: 20 }}>
        <SolidCard padding={40} radius={20}>
          <div style={{ textAlign: 'center', color: T.label2 }}>Acesso restrito a barbeiros</div>
        </SolidCard>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
        <div style={{ color: T.label2, fontSize: 15 }}>Carregando fila...</div>
      </div>
    );
  }

  const filaEmExecucao = fila.filter(t => t.status === 'in_service');
  const filaEspera = fila.filter(t => t.status === 'queued');

  const inputStyle = { width: '100%', padding: '14px 16px', borderRadius: 14, background: T.fill, border: `0.5px solid ${T.separator}`, color: T.label, fontSize: 16, fontFamily: 'inherit', outline: 'none' };

  return (
    <div style={{ padding: '0 0 40px' }}>
      <LargeTitle
        title="Fila"
        subtitle={`${filaEmExecucao.length} em execucao · ${filaEspera.length} aguardando`}
        trailing={
          <button onClick={() => { setAtualizando(true); carregarFila(); }} disabled={atualizando}
            style={{
              border: 'none', cursor: 'pointer', width: 38, height: 38, borderRadius: 19,
              background: T.accent, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(10,132,255,0.4)',
              opacity: atualizando ? 0.6 : 1,
            }}>
            <RefreshCcw size={20} className={atualizando ? 'animate-spin' : ''} />
          </button>
        }
      />

      <div style={{ padding: '8px 20px 0' }}>
        <HeroCard gradient={`linear-gradient(135deg, ${T.accent} 0%, ${T.indigo} 100%)`} shadowColor="rgba(10,132,255,0.35)">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, opacity: 0.85 }}>
            <Clock size={14} />Tempo medio de espera
          </div>
          <div className="tnum" style={{ fontSize: 44, fontWeight: 700, letterSpacing: '-0.035em', marginTop: 6, lineHeight: 1 }}>
            {filaEspera.length > 0 ? `~${filaEspera.length * 15} min` : '0 min'}
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 14, fontSize: 12 }}>
            <div>
              <div style={{ opacity: 0.7, fontSize: 11 }}>Em execucao</div>
              <div className="tnum" style={{ fontSize: 17, fontWeight: 600, marginTop: 2 }}>{filaEmExecucao.length}</div>
            </div>
            <div style={{ width: 0.5, background: 'rgba(255,255,255,0.25)' }} />
            <div>
              <div style={{ opacity: 0.7, fontSize: 11 }}>Na fila</div>
              <div className="tnum" style={{ fontSize: 17, fontWeight: 600, marginTop: 2 }}>{filaEspera.length}</div>
            </div>
          </div>
        </HeroCard>
      </div>

      {/* Em execucao */}
      {filaEmExecucao.length > 0 && (
        <div style={{ padding: '4px 20px 0' }}>
          <SectionHeader title="Em execucao" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filaEmExecucao.map(ticket => (
              <SolidCard key={ticket.id} padding={16} radius={18}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Avatar name={ticket.cliente?.nome || 'Cliente'} size={44} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ fontSize: 16, fontWeight: 600, color: T.label }}>{ticket.cliente?.nome || 'Cliente'}</div>
                      <StatusBadge status="live" label="EXECUTANDO" />
                    </div>
                    {ticket.cliente?.telefone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: T.label2, marginTop: 2 }}>
                        <Phone size={11} /> {ticket.cliente.telefone}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                  <button onClick={() => abrirComandaFinalizacao(ticket)} style={{
                    border: 'none', cursor: 'pointer', padding: '10px 20px', borderRadius: 14,
                    background: T.success, color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(48,209,88,0.3)',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}><CheckCircle size={15} /> Finalizar</button>
                </div>
              </SolidCard>
            ))}
          </div>
        </div>
      )}

      {/* Aguardando */}
      <div style={{ padding: '4px 20px 0' }}>
        <SectionHeader title={`Aguardando (${filaEspera.length})`} />
        {filaEspera.length === 0 ? (
          <SolidCard padding={40} radius={20}>
            <div style={{ textAlign: 'center', color: T.label3, fontSize: 15 }}>Nenhum cliente na fila</div>
          </SolidCard>
        ) : (
          <SolidCard padding={0} radius={20}>
            {filaEspera.map((ticket, i) => (
              <div key={ticket.id} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: 14,
                borderBottom: i < filaEspera.length - 1 ? `0.5px solid ${T.separator}` : 'none',
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 10,
                  background: T.fill, color: T.label,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 15, flexShrink: 0,
                }}>{ticket.position}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: T.label }}>{ticket.cliente?.nome || 'Cliente'}</div>
                  {ticket.cliente?.telefone && (
                    <div style={{ fontSize: 12, color: T.label2, marginTop: 1 }}>{ticket.cliente.telefone}</div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => abrirCancelamento(ticket)} style={{
                    width: 36, height: 36, borderRadius: 10, border: 'none', cursor: 'pointer',
                    background: 'rgba(255,59,48,0.12)', color: T.danger,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}><Ban size={15} /></button>
                  {i === 0 && (
                    <button onClick={() => iniciarAtendimento(ticket.id)} style={{
                      border: 'none', cursor: 'pointer', padding: '8px 16px', borderRadius: 12,
                      background: T.accent, color: '#fff', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                      display: 'flex', alignItems: 'center', gap: 4,
                      boxShadow: '0 4px 12px rgba(10,132,255,0.3)',
                    }}><Play size={13} /> Iniciar</button>
                  )}
                </div>
              </div>
            ))}
          </SolidCard>
        )}
      </div>

      {/* Modal Finalizar — cria comanda pendente */}
      {modalComanda && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          zIndex: 9999,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: T.surface, borderRadius: '24px 24px 0 0', width: '100%', maxWidth: 500,
            maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: T.shadowStrong,
          }}>
            <div style={{ padding: 20, borderBottom: `0.5px solid ${T.separator}`, flexShrink: 0 }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: T.fill2, margin: '0 auto 16px' }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: T.label }}>Finalizar atendimento</div>
                  <div style={{ fontSize: 14, color: T.label2, marginTop: 2 }}>
                    {ticketFinalizando?.cliente?.nome || 'Cliente'}
                  </div>
                </div>
                <button onClick={fecharModalComanda} style={{
                  width: 36, height: 36, borderRadius: 18, border: 'none',
                  background: T.fill, color: T.label, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}><X size={18} /></button>
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.label2, marginBottom: 8 }}>Servicos realizados *</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {servicos.filter(s => s.ativo !== false).map(s => {
                    const selected = srvSel.includes(s.id);
                    return (
                      <button key={s.id} onClick={() => toggleServico(s.id)} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '14px 16px', borderRadius: 14, border: 'none', cursor: 'pointer',
                        background: selected ? T.accentSoft : T.fill,
                        transition: 'all 200ms',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 22, height: 22, borderRadius: 6,
                            border: `2px solid ${selected ? T.accent : T.separator}`,
                            background: selected ? T.accent : 'transparent',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 200ms',
                          }}>
                            {selected && <CheckCircle size={14} color="#fff" />}
                          </div>
                          <span style={{ fontSize: 15, fontWeight: 500, color: T.label }}>{s.nome}</span>
                        </div>
                        <span className="tnum" style={{ fontSize: 15, fontWeight: 600, color: selected ? T.accent : T.label2 }}>
                          {fmtBRL(s.preco)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.label2, marginBottom: 6 }}>Observacoes</label>
                <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)} placeholder="Opcional..."
                  rows={2} style={{ ...inputStyle, resize: 'none' }} />
              </div>

              {srvSel.length > 0 && (
                <div style={{
                  background: T.accentSoft, borderRadius: 14, padding: '14px 18px',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: T.accent }}>Total</span>
                  <span className="tnum" style={{ fontSize: 20, fontWeight: 700, color: T.accent }}>{fmtBRL(totalComanda)}</span>
                </div>
              )}

              <div style={{ background: 'rgba(255,159,10,0.1)', borderRadius: 12, padding: '10px 14px' }}>
                <div style={{ fontSize: 12, color: T.warning }}>O cliente pagara no caixa apos o atendimento.</div>
              </div>
            </div>

            <div style={{ padding: 20, borderTop: `0.5px solid ${T.separator}`, flexShrink: 0 }}>
              <button onClick={handleFinalizar} disabled={salvandoComanda || srvSel.length === 0} style={{
                width: '100%', padding: 16, borderRadius: 14, border: 'none',
                background: (salvandoComanda || srvSel.length === 0) ? T.fill2 : T.success,
                color: '#fff', fontFamily: 'inherit', fontSize: 16, fontWeight: 600,
                cursor: (salvandoComanda || srvSel.length === 0) ? 'not-allowed' : 'pointer',
                boxShadow: (salvandoComanda || srvSel.length === 0) ? 'none' : '0 4px 14px rgba(48,209,88,0.35)',
              }}>
                {salvandoComanda ? 'Salvando...' : 'Finalizar e criar comanda'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Cancelar */}
      {modalCancelar && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          zIndex: 9999,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: T.surface, borderRadius: '24px 24px 0 0', width: '100%', maxWidth: 500,
            display: 'flex', flexDirection: 'column', boxShadow: T.shadowStrong,
          }}>
            <div style={{ padding: 20, borderBottom: `0.5px solid ${T.separator}` }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: T.fill2, margin: '0 auto 16px' }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: T.danger }}>Cancelar atendimento</div>
                  <div style={{ fontSize: 14, color: T.label2, marginTop: 2 }}>
                    {ticketCancelando?.cliente?.nome || 'Cliente'}
                  </div>
                </div>
                <button onClick={fecharModalCancelar} style={{
                  width: 36, height: 36, borderRadius: 18, border: 'none',
                  background: T.fill, color: T.label, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}><X size={18} /></button>
              </div>
            </div>

            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ background: 'rgba(255,59,48,0.08)', borderRadius: 14, padding: '14px 16px' }}>
                <div style={{ fontSize: 13, color: T.danger }}>
                  O cliente sera removido da fila. Informe o motivo abaixo.
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.label2, marginBottom: 6 }}>Motivo do cancelamento *</label>
                <textarea
                  value={motivoCancelamento}
                  onChange={e => setMotivoCancelamento(e.target.value)}
                  placeholder="Ex: Cliente desistiu, nao compareceu..."
                  rows={3}
                  style={{ ...inputStyle, resize: 'none' }}
                />
              </div>
            </div>

            <div style={{ padding: 20, display: 'flex', gap: 10, borderTop: `0.5px solid ${T.separator}` }}>
              <button onClick={fecharModalCancelar} disabled={cancelando} style={{
                flex: 1, padding: 15, borderRadius: 14, border: `1px solid ${T.separator}`,
                background: 'transparent', color: T.label, fontFamily: 'inherit', fontSize: 15, fontWeight: 600, cursor: 'pointer',
              }}>Voltar</button>
              <button onClick={handleCancelar} disabled={cancelando || !motivoCancelamento.trim()} style={{
                flex: 1, padding: 15, borderRadius: 14, border: 'none',
                background: (cancelando || !motivoCancelamento.trim()) ? T.fill2 : T.danger,
                color: '#fff', fontFamily: 'inherit', fontSize: 15, fontWeight: 600,
                cursor: (cancelando || !motivoCancelamento.trim()) ? 'not-allowed' : 'pointer',
              }}>
                {cancelando ? 'Cancelando...' : 'Confirmar cancelamento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilaPage;
